import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { Dialog, DialogContent, DialogTrigger, DialogTitle, DialogHeader } from '@/shared/components/ui/dialog';
import { toast } from 'sonner';
import {
  LayoutDashboard,
  Palette,
  Globe,
  History,
  Eye,
  Rocket,
  Settings,
  FileText,
  Layers,
  Languages,
  Clock,
  CheckCircle,
  AlertCircle,
  Sparkles,
  Link2,
  Plus } from
'lucide-react';
import useLandingConfig from '../hooks/useLandingConfig';

// Static thumbnail mapping
const getSectionThumbnail = (sectionKey) => {
  const map = {
    hero: '/images/landing/hero_horizon_sunset.png',
    features_1: '/images/landing/it_department_ai.png',
    features_2: '/images/landing/diamond_commercial_team.png',
    modules: '/images/landing/diamond_product_vision.png',
    cta: '/images/landing/diamond_client_acquisition.png',
    pricing: '/images/landing/magic-portfolio-clean.png',
    faq: '/images/landing/diamond_sales_strategy.png',
    footer: '/images/landing/hero_horizon_sunset.png'
  };
  return map[sectionKey] || '/images/landing/hero_horizon_sunset.png';
};

const SectionThumbnail = ({ sectionKey }) => {
  const THUMB_W = 44;   // px — display width
  const THUMB_H = 28;   // px — display height
  const imgSrc = getSectionThumbnail(sectionKey);

  return (
    <div
      style={{ width: THUMB_W, height: THUMB_H, flexShrink: 0, overflow: 'hidden', position: 'relative' }}
      className="rounded border border-slate-300 dark:border-slate-600 bg-slate-100 flex items-center justify-center"
    >
      <img
        src={imgSrc}
        alt={`Thumbnail for ${sectionKey}`}
        className="w-full h-full object-cover"
      />
    </div>
  );
};


export const LandingSetupDashboard = () => {const { t } = useTranslation("landing");
  const {
    config,
    sections,
    loading,
    initializeFromTemplate,
    publish,
    getVersions,
    getAuditLogs,
    updateSection
  } = useLandingConfig();

  const toggleVisibility = async (sectionId, currentVisibility) => {
    const result = await updateSection(sectionId, { is_visible: !currentVisibility });
    if (result.success) {
      toast.success('Section visibility updated!');
    } else {
      toast.error(result.error || 'Failed to update visibility');
    }
  };

  const [versions, setVersions] = useState([]);
  const [recentLogs, setRecentLogs] = useState([]);
  const [stats, setStats] = useState({
    totalSections: 0,
    totalBlocks: 0,
    lastPublished: null,
    currentVersion: null
  });

  useEffect(() => {
    const loadDashboardData = async () => {
      const versionsResult = await getVersions();
      if (versionsResult.success) {
        setVersions(versionsResult.versions);
        const published = versionsResult.versions.find((v) => v.is_published);
        if (published) {
          setStats((prev) => ({
            ...prev,
            lastPublished: published.published_at,
            currentVersion: published.version_label
          }));
        }
      }

      const logsResult = await getAuditLogs({ limit: 5 });
      if (logsResult.success) {
        setRecentLogs(logsResult.logs);
      }
    };

    if (config) {
      loadDashboardData();
      const totalBlocks = sections.reduce((acc, s) => acc + (s.content_blocks?.length || 0), 0);
      setStats((prev) => ({
        ...prev,
        totalSections: sections.length,
        totalBlocks
      }));
    }
  }, [config, sections, getVersions, getAuditLogs]);

  const handleInitialize = async () => {
    const result = await initializeFromTemplate();
    if (result.success) {
      toast.success('Landing page initialized from template!');
    } else {
      toast.error(result.error || 'Failed to initialize');
    }
  };

  const handlePublish = async () => {
    const versionLabel = `v${(versions.length || 0) + 1}.0`;
    const result = await publish(versionLabel, 'Published from dashboard');
    if (result.success) {
      toast.success(`Published as ${versionLabel}!`);
      const versionsResult = await getVersions();
      if (versionsResult.success) {
        setVersions(versionsResult.versions);
      }
    } else {
      toast.error(result.error || 'Failed to publish');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>);

  }

  if (!config) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center gap-3 mb-6">
          <LayoutDashboard className="w-8 h-8 text-blue-500" />
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">{t("landingPageSetup", "Landing Page Setup")}</h1>
            <p className="text-slate-500 dark:text-slate-400">{t("configureYourPublicfacing", "Configure your public-facing landing page")}</p>
          </div>
        </div>

        <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mb-4">
              <Rocket className="w-8 h-8 text-blue-500" />
            </div>
            <CardTitle className="text-slate-900 dark:text-white">{t("getStarted", "Get Started")}</CardTitle>
            <CardDescription className="text-slate-500 dark:text-slate-400">{t("initializeYourLanding", "Initialize your landing page with our default template, then customize it to match your brand.")}

            </CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center">
            <Button onClick={handleInitialize} size="lg" className="gap-2">
              <Plus className="w-5 h-5" />{t("initializeFromTemplate", "Initialize from Template")}

            </Button>
          </CardContent>
        </Card>
      </div>);

  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <LayoutDashboard className="w-8 h-8 text-blue-500" />
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">{t("landingPageSetup", "Landing Page Setup")}</h1>
            <p className="text-slate-500 dark:text-slate-400">{config.site_name} - {config.tagline || 'Configure your landing page'}</p>
          </div>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" className="gap-2" asChild>
            <a href="/landing-preview" target="_blank" rel="noopener noreferrer">
              <Eye className="w-4 h-4" />{t("preview", "Preview")}

            </a>
          </Button>
          <Button onClick={handlePublish} className="gap-2 bg-green-600 hover:bg-green-700">
            <Rocket className="w-4 h-4" />{t("publish", "Publish")}

          </Button>
        </div>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">{t("status", "Status")}</p>
                <div className="flex items-center gap-2 mt-1">
                  {config.is_published ?
                  <>
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span className="text-green-600 dark:text-green-500 font-medium">{t("published", "Published")}</span>
                    </> :

                  <>
                      <AlertCircle className="w-5 h-5 text-yellow-500" />
                      <span className="text-yellow-600 dark:text-yellow-500 font-medium">{t("draft", "Draft")}</span>
                    </>
                  }
                </div>
              </div>
              {stats.currentVersion &&
              <Badge variant="outline" className="text-blue-600 dark:text-blue-400 border-blue-400">
                  {stats.currentVersion}
                </Badge>
              }
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">{t("sections", "Sections")}</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">{stats.totalSections}</p>
              </div>
              <Layers className="w-8 h-8 text-slate-400 dark:text-slate-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">{t("contentBlocks", "Content Blocks")}</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">{stats.totalBlocks}</p>
              </div>
              <FileText className="w-8 h-8 text-slate-400 dark:text-slate-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">{t("languages", "Languages")}</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">{config.enabled_languages?.length || 1}</p>
              </div>
              <Languages className="w-8 h-8 text-slate-400 dark:text-slate-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 hover:border-blue-500/50 transition-colors cursor-pointer" data-testid="section-editor-card">
          <a href="/admin/landing/sections">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
                  <Layers className="w-6 h-6 text-blue-500" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 dark:text-white">{t("sectionEditor", "Section Editor")}</h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{t("managePageSections", "Manage page sections")}</p>
                </div>
              </div>
            </CardContent>
          </a>
        </Card>

        <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 hover:border-purple-500/50 transition-colors cursor-pointer" data-testid="branding-card">
          <a href="/admin/landing/branding">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center">
                  <Palette className="w-6 h-6 text-purple-500" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 dark:text-white">{t("branding", "Branding")}</h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{t("colorsLogoTheme", "Colors, logo, theme")}</p>
                </div>
              </div>
            </CardContent>
          </a>
        </Card>

        <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 hover:border-green-500/50 transition-colors cursor-pointer" data-testid="translations-card">
          <a href="/admin/landing/translations">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center">
                  <Globe className="w-6 h-6 text-green-500" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 dark:text-white">{t("translations", "Translations")}</h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{t("multilanguageContent", "Multi-language content")}</p>
                </div>
              </div>
            </CardContent>
          </a>
        </Card>

        <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 hover:border-pink-500/50 transition-colors cursor-pointer" data-testid="skins-card">
          <a href="/admin/landing/skins">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-pink-500/20 rounded-lg flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-pink-500" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 dark:text-white">{t("skins", "Skins & Temas")}</h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{t("landingSkinsDescription", "Selecione e importe skins ZIP")}</p>
                </div>
              </div>
            </CardContent>
          </a>
        </Card>

        <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 hover:border-orange-500/50 transition-colors cursor-pointer" data-testid="versions-card">
          <a href="/admin/landing/versions">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-orange-500/20 rounded-lg flex items-center justify-center">
                  <History className="w-6 h-6 text-orange-500" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 dark:text-white">{t("versions", "Versions")}</h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{t("historyRollback", "History & rollback")}</p>
                </div>
              </div>
            </CardContent>
          </a>
        </Card>

        {/* Module Presentation Links — NEW */}
        <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 hover:border-cyan-500/50 transition-colors cursor-pointer" data-testid="module-links-card">
          <a href="/admin/landing/sections">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                  <Link2 className="w-6 h-6 text-cyan-500 dark:text-cyan-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 dark:text-white">{t("moduleLinks", "Module Links")}</h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{t("linkToPresentations", "Link modules to presentations")}</p>
                </div>
              </div>
            </CardContent>
          </a>
        </Card>
      </div>

      {/* Recent Activity & Sections Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
          <CardHeader>
            <CardTitle className="text-slate-900 dark:text-white flex items-center gap-2">
              <Clock className="w-5 h-5" />{t("recentActivity", "Recent Activity")}

            </CardTitle>
          </CardHeader>
          <CardContent>
            {recentLogs.length === 0 ?
            <p className="text-slate-500 dark:text-slate-400 text-center py-4">{t("noRecentActivity", "No recent activity")}</p> :

            <div className="space-y-3">
                {recentLogs.map((log, index) =>
              <div key={index} className="flex items-start gap-3 p-3 bg-slate-100 dark:bg-slate-700/30 rounded-lg">
                    <div className={`w-2 h-2 rounded-full mt-2 ${
                log.action === 'publish' ? 'bg-green-500' :
                log.action === 'create' ? 'bg-blue-500' :
                log.action === 'update' ? 'bg-yellow-500' :
                log.action === 'delete' ? 'bg-red-500' : 'bg-slate-400'}`
                } />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-900 dark:text-white truncate">{log.description}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {log.user_email} • {new Date(log.timestamp).toLocaleString()}
                      </p>
                    </div>
                  </div>
              )}
              </div>
            }
          </CardContent>
        </Card>

        <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
          <CardHeader>
            <CardTitle className="text-slate-900 dark:text-white flex items-center gap-2">
              <Layers className="w-5 h-5" />{t("pageSections", "Page Sections")}

            </CardTitle>
          </CardHeader>
          <CardContent>
            {sections.length === 0 ?
            <p className="text-slate-500 dark:text-slate-400 text-center py-4">{t("noSectionsConfigured", "No sections configured")}</p> :

            <div className="space-y-2">
                {sections.map((section, index) =>
              <div key={section.id} className="flex items-center justify-between p-2.5 bg-slate-100 dark:bg-slate-700/30 rounded-lg">
                    <div className="flex items-center gap-2.5">
                      <span className="w-5 h-5 bg-slate-200 dark:bg-slate-600 rounded-full flex items-center justify-center text-[10px] text-slate-700 dark:text-slate-300 flex-shrink-0">
                        {index + 1}
                      </span>
                      {/* Tiny section thumbnail */}
                      <Dialog>
                        <DialogTrigger asChild>
                          <div className="cursor-pointer hover:opacity-80 transition-opacity" title={`Preview ${section.section_key} section`}>
                            <SectionThumbnail sectionKey={section.section_key} />
                          </div>
                        </DialogTrigger>
                        <DialogContent className="max-w-[90vw] md:max-w-[800px] w-full p-0 overflow-hidden bg-transparent border-0 shadow-none">
                          <DialogHeader className="sr-only"><DialogTitle>Preview {section.section_key}</DialogTitle></DialogHeader>
                          <div className="relative w-full flex items-center justify-center bg-black/90 rounded-xl p-4">
                            <img
                              src={getSectionThumbnail(section.section_key)}
                              alt={`Full preview for ${section.section_key}`}
                              className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
                            />
                          </div>
                        </DialogContent>
                      </Dialog>
                      <div>
                        <p className="text-sm font-medium text-slate-900 dark:text-white capitalize">{section.section_key.replace(/_/g, ' ')}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">{section.section_type}</p>
                      </div>
                    </div>
                    <Badge 
                      variant={section.is_visible ? 'default' : 'secondary'} 
                      className="text-xs cursor-pointer hover:opacity-85 select-none transition-all duration-150"
                      onClick={() => toggleVisibility(section.id, section.is_visible)}
                      data-testid={`toggle-visibility-${section.section_key}`}
                      title={section.is_visible ? 'Click to Hide' : 'Click to Show'}
                    >
                      {section.is_visible ? 'Visible' : 'Hidden'}
                    </Badge>
                  </div>
              )}
              </div>
            }
          </CardContent>
        </Card>
      </div>
    </div>);

};

export default LandingSetupDashboard;